/**
 * Auth API
 * 인증 및 회원가입 관련 API 서비스
 */
import { apiClient } from '@/shared/api/client';
import {
  PartnerPortalLoginRequest,
  PartnerPortalLoginResponse,
  CheckSupplierIdRequest,
  CheckSupplierIdResponse,
  HeadquartersRegisterRequest,
  SupplierRegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyHeadquartersCodeRequest,
  VerifyHeadquartersCodeResponse,
  GetUserInfoResponse,
} from '../types/auth.types';

const BASE_PATH = '/api/v1/partner-portal/auth';

/**
 * Auth API 서비스
 * 로그인, 회원가입, 비밀번호 관리 등 인증 관련 API 제공
 */
export const authApi = {
  /**
   * 로그인 요청
   * @param request - 로그인 요청 데이터 (userId, password)
   * @returns 로그인 응답 (토큰, 사용자 정보)
   */
  login: async (request: PartnerPortalLoginRequest): Promise<PartnerPortalLoginResponse> => {
    const response = await apiClient.post<PartnerPortalLoginResponse>(`${BASE_PATH}/login`, request);
    return response.data;
  },

  /**
   * 협력사 ID 확인
   * @param request - 협력사 ID 확인 요청 데이터
   * @returns 협력사 존재 여부 및 정보
   */
  checkSupplierId: async (request: CheckSupplierIdRequest): Promise<CheckSupplierIdResponse> => {
    const response = await apiClient.post<CheckSupplierIdResponse>(`${BASE_PATH}/check-supplier-id`, request);
    return response.data;
  },

  /**
   * 본사 관리자 회원가입
   * @param request - 본사 회원가입 요청 데이터
   * @returns 회원가입 결과
   */
  registerHeadquarters: async (request: HeadquartersRegisterRequest): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>(`${BASE_PATH}/register/headquarters`, request);
    return response.data;
  },

  /**
   * 협력사 회원가입
   * @param request - 협력사 회원가입 요청 데이터
   * @returns 회원가입 결과
   */
  registerSupplier: async (request: SupplierRegisterRequest): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>(`${BASE_PATH}/register/supplier`, request);
    return response.data;
  },

  /**
   * 비밀번호 찾기 - 재설정 이메일 전송
   * @param request - 비밀번호 찾기 요청 데이터 (userId, emailInfo)
   * @returns 이메일 전송 결과
   */
  forgotPassword: async (request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    const response = await apiClient.post<ForgotPasswordResponse>(`${BASE_PATH}/forgot-password`, request);
    return response.data;
  },

  /**
   * 비밀번호 재설정
   * @param request - 비밀번호 재설정 요청 데이터 (token, newPassword)
   * @returns 비밀번호 재설정 결과
   */
  resetPassword: async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    const response = await apiClient.post<ResetPasswordResponse>(`${BASE_PATH}/reset-password`, request);
    return response.data;
  },

  /**
   * 본사 직원 확인 코드 검증
   * @param request - 확인 코드 검증 요청 데이터
   * @returns 코드 유효성 검증 결과
   */
  verifyHeadquartersCode: async (request: VerifyHeadquartersCodeRequest): Promise<VerifyHeadquartersCodeResponse> => {
    const response = await apiClient.post<VerifyHeadquartersCodeResponse>(`${BASE_PATH}/verify-headquarters-code`, request);
    return response.data;
  },

  /**
   * 사용자 정보 조회
   * @param userId - 조회할 사용자 ID
   * @returns 사용자 상세 정보
   */
  getUserInfo: async (userId: string): Promise<GetUserInfoResponse> => {
    const response = await apiClient.get<GetUserInfoResponse>(`${BASE_PATH}/user/${userId}`);
    return response.data;
  },
};
